/* eslint-disable ember/no-observers */
import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import EmberObject, { action, computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import { observes } from "@ember-decorators/object";
import { Promise } from "rsvp";
import { ajax } from "discourse/lib/ajax";
import { removeCookie } from "discourse/lib/cookie";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import NameValidationHelper from "discourse/lib/name-validation-helper";
import PasswordValidationHelper from "discourse/lib/password-validation-helper";
import { autoTrackedArray } from "discourse/lib/tracked-tools";
import { userPath } from "discourse/lib/url";
import UserFieldsValidationHelper from "discourse/lib/user-fields-validation-helper";
import UsernameValidationHelper from "discourse/lib/username-validation-helper";
import { emailValid } from "discourse/lib/utilities";
import { findAll } from "discourse/models/login-method";
import User from "discourse/models/user";
import { i18n } from "discourse-i18n";

export default class SignupPageController extends Controller {
  @service site;
  @service siteSettings;
  @service login;

  @tracked accountName;
  @tracked accountPassword;
  @tracked accountEmail;
  @tracked accountUsername;
  @tracked isDeveloper = false;
  @tracked authOptions;
  @tracked skipConfirmation;
  @tracked serverAccountEmail;
  @tracked serverEmailValidation;
  @tracked codeSignupStep = "email";
  @autoTrackedArray rejectedEmails = [];

  accountChallenge = 0;
  accountHoneypot = 0;
  formSubmitted = false;
  prefilledUsername = null;
  maskPassword = true;
  emailValidationVisible = false;
  nameValidationHelper = new NameValidationHelper(this);
  usernameValidationHelper = new UsernameValidationHelper({
    getAccountEmail: () => this.accountEmail,
    getAccountUsername: () => this.accountUsername,
    getPrefilledUsername: () => this.prefilledUsername,
    getAuthOptionsUsername: () => this.authOptions?.username,
    getForceValidationReason: () => this.forceValidationReason,
    siteSettings: this.siteSettings,
    isInvalid: () => this.isDestroying,
    updateIsDeveloper: (isDeveloper) => (this.isDeveloper = isDeveloper),
    updateUsernames: (username) => {
      this.accountUsername = username;
      this.prefilledUsername = username;
    },
  });
  passwordValidationHelper = new PasswordValidationHelper(this);
  userFieldsValidationHelper = new UserFieldsValidationHelper({
    getUserFields: () =>
      this.site.get("user_fields")?.filter((f) => f.show_on_signup),
    getAccountPassword: () => this.accountPassword,
    showValidationOnInit: false,
  });

  @computed("authOptions")
  get hasAuthOptions() {
    return !isEmpty(this.authOptions);
  }

  @computed("siteSettings.enable_local_logins")
  get canCreateLocal() {
    return this.siteSettings.enable_local_logins;
  }

  @computed("siteSettings.require_invite_code")
  get requireInviteCode() {
    return this.siteSettings.require_invite_code;
  }

  @dependentKeyCompat
  get userFields() {
    return this.userFieldsValidationHelper.userFields;
  }

  @dependentKeyCompat
  get userFieldsValidation() {
    return this.userFieldsValidationHelper.userFieldsValidation;
  }

  @dependentKeyCompat
  get usernameValidation() {
    return this.usernameValidationHelper.usernameValidation;
  }

  @dependentKeyCompat
  get passwordValidation() {
    return this.passwordValidationHelper.passwordValidation;
  }

  get nameTitle() {
    return this.nameValidationHelper.nameTitle;
  }

  get nameValidation() {
    return this.nameValidationHelper.nameValidation;
  }

  @dependentKeyCompat
  get forceValidationReason() {
    return this.nameValidationHelper.forceValidationReason;
  }

  @computed("hasAuthOptions", "canCreateLocal", "skipConfirmation")
  get showCreateForm() {
    return (
      (this.hasAuthOptions || this.canCreateLocal) &&
      !this.skipConfirmation &&
      !this.showCodeSignupForm
    );
  }

  @computed("hasAuthOptions", "canCreateLocal", "skipConfirmation")
  get showCodeSignupForm() {
    return (
      this.siteSettings.enable_local_logins_via_code &&
      this.canCreateLocal &&
      !this.hasAuthOptions &&
      !this.skipConfirmation
    );
  }

  get codeSignupOnEmailStep() {
    return this.codeSignupStep === "email";
  }

  @computed("site.desktopView", "hasAuthOptions")
  get showExternalLoginButtons() {
    return this.site?.desktopView && !this.hasAuthOptions;
  }

  @computed("formSubmitted")
  get submitDisabled() {
    return this.formSubmitted;
  }

  @computed(
    "userFields",
    "hasAtLeastOneLoginButton",
    "hasAuthOptions",
    "showCodeSignupForm"
  )
  get bodyClasses() {
    const classes = [];
    if (this.userFields) {
      classes.push("has-user-fields");
    }
    if (this.hasAtLeastOneLoginButton && !this.hasAuthOptions) {
      classes.push("has-alt-auth");
    }
    if (!this.canCreateLocal) {
      classes.push("no-local-logins");
    }
    if (this.showCodeSignupForm) {
      classes.push("passwordless-signup");
    }
    return classes.join(" ");
  }

  @computed("authOptions", "authOptions.can_edit_username")
  get usernameDisabled() {
    return this.authOptions && !this.authOptions?.can_edit_username;
  }

  @computed("authOptions", "authOptions.can_edit_name", "authOptions.name")
  get nameDisabled() {
    return (
      this.authOptions &&
      !this.authOptions?.can_edit_name &&
      this.authOptions?.name &&
      this.authOptions?.name?.length > 0
    );
  }

  @computed
  get showFullname() {
    return this.site.full_name_visible_in_signup;
  }

  @computed
  get fullnameRequired() {
    return this.site.full_name_required_for_signup;
  }

  @computed(
    "emailValidation.ok",
    "emailValidation.reason",
    "emailValidationVisible"
  )
  get showEmailValidation() {
    return (
      this.emailValidation?.ok ||
      (this.emailValidation?.reason && this.emailValidationVisible)
    );
  }

  get showPasswordValidation() {
    return this.passwordValidation.ok || this.passwordValidation.reason;
  }

  get showUsernameInstructions() {
    return (
      this.siteSettings.show_signup_form_username_instructions &&
      !this.usernameValidation.reason
    );
  }

  get passwordRequired() {
    return isEmpty(this.authOptions?.auth_provider);
  }

  @computed
  get disclaimerHtml() {
    if (this.site.tos_url && this.site.privacy_policy_url) {
      return i18n("create_account.disclaimer", {
        tos_link: this.site.tos_url,
        privacy_link: this.site.privacy_policy_url,
      });
    }
  }

  // Check the email address
  @dependentKeyCompat
  get emailValidation() {
    const failedAttrs = {
      failed: true,
      ok: false,
      element: document.querySelector("#new-account-email"),
    };

    if (
      this.serverAccountEmail === this.accountEmail &&
      this.serverEmailValidation
    ) {
      return this.serverEmailValidation;
    }

    // If blank, fail without a reason
    if (isEmpty(this.accountEmail)) {
      return EmberObject.create(
        Object.assign(failedAttrs, {
          message: i18n("user.email.required"),
          reason: this.forceValidationReason
            ? i18n("user.email.required")
            : null,
        })
      );
    }

    if (
      this.rejectedEmails.includes(this.accountEmail) ||
      !emailValid(this.accountEmail)
    ) {
      return EmberObject.create(
        Object.assign(failedAttrs, {
          reason: i18n("user.email.invalid"),
        })
      );
    }

    if (
      this.authOptions?.email === this.accountEmail &&
      this.authOptions?.email_valid &&
      !isEmpty(this.authOptions?.auth_provider)
    ) {
      const provider = this.authProviderDisplayName(
        this.authOptions.auth_provider
      );

      if (!isEmpty(provider)) {
        return EmberObject.create({
          ok: true,
          reason: i18n("user.email.authenticated", { provider }),
        });
      }
    }

    return EmberObject.create({
      ok: true,
      reason: i18n("user.email.ok"),
    });
  }

  get emailDisabled() {
    return (
      this.authOptions?.email === this.accountEmail &&
      this.authOptions?.email_valid
    );
  }

  // Determines whether at least one login button is enabled
  @computed
  get hasAtLeastOneLoginButton() {
    return findAll().length > 0;
  }

  @computed("hasAtLeastOneLoginButton", "canCreateLocal", "hasAuthOptions")
  get hasNoLoginOptions() {
    return (
      !this.hasAtLeastOneLoginButton &&
      !this.canCreateLocal &&
      !this.hasAuthOptions
    );
  }

  @computed(
    "authOptions",
    "hasAtLeastOneLoginButton",
    "showCodeSignupForm",
    "codeSignupStep"
  )
  get showRightSide() {
    if (this.showCodeSignupForm && !this.codeSignupOnEmailStep) {
      return false;
    }
    return !this.authOptions && this.hasAtLeastOneLoginButton;
  }

  @computed("authOptions")
  get progressBarStep() {
    return this.authOptions ? "activate" : "signup";
  }

  @computed("authOptions.associate_url", "authOptions.auth_provider")
  get associateHtml() {
    if (!this.authOptions?.associate_url) {
      return;
    }
    return i18n("create_account.associate", {
      associate_link: this.authOptions?.associate_url,
      provider: i18n(`login.${this.authOptions?.auth_provider}.name`),
    });
  }

  @bind
  actionOnEnter(event) {
    if (!this.submitDisabled && event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.createAccount();
      return false;
    }
  }

  @bind
  selectKitFocus(event) {
    const target = document.getElementById(event.target.getAttribute("for"));
    if (target?.classList.contains("select-kit")) {
      event.preventDefault();
      target.querySelector(".select-kit-header").click();
    }
  }

  @action
  updateCodeSignupStep(step) {
    this.codeSignupStep = step;
  }

  @action
  setAccountUsername(event) {
    this.accountUsername = event.target.value;
  }

  @action
  checkEmailAvailability() {
    if (this.emailValidation.reason) {
      this.set("emailValidationVisible", true);
    } else {
      this.set("emailValidationVisible", false);
    }

    if (
      !this.emailValidation.ok ||
      this.serverAccountEmail === this.accountEmail
    ) {
      return;
    }

    return User.checkEmail(this.accountEmail)
      .then((result) => {
        if (this.isDestroying) {
          return;
        }

        if (result.failed) {
          this.setProperties({
            serverAccountEmail: this.accountEmail,
            serverEmailValidation: EmberObject.create({
              failed: true,
              element: document.querySelector("#new-account-email"),
              reason: result.errors[0],
            }),
          });
        } else {
          this.setProperties({
            serverAccountEmail: this.accountEmail,
            serverEmailValidation: EmberObject.create({
              ok: true,
              reason: i18n("user.email.ok"),
            }),
          });
        }
      })
      .catch(() => {
        this.setProperties({
          serverAccountEmail: null,
          serverEmailValidation: null,
        });
      });
  }

  authProviderDisplayName(name) {
    return (
      findAll()
        .find((p) => p.name === name)
        ?.get("prettyName") || name
    );
  }

  @observes("emailValidation", "accountEmail")
  prefillUsername() {
    if (this.prefilledUsername) {
      // If username field has been filled automatically, and email field just changed,
      // then remove the username.
      if (this.accountUsername === this.prefilledUsername) {
        this.accountUsername = "";
      }
      this.set("prefilledUsername", null);
    }
    if (
      this.get("emailValidation.ok") &&
      (isEmpty(this.accountUsername) || this.authOptions?.email)
    ) {
      // If email is valid and username has not been entered yet,
      // or email and username were filled automatically by 3rd party auth,
      // then look for a registered username that matches the email.
      discourseDebounce(
        this,
        () => this.usernameValidationHelper.fetchExistingUsername(),
        500
      );
    }
  }

  fetchConfirmationValue() {
    if (this._challengeDate === undefined && this._hpPromise) {
      // Request already in progress
      return this._hpPromise;
    }

    this._hpPromise = ajax("/session/hp.json")
      .then((json) => {
        if (this.isDestroying) {
          return;
        }

        this._challengeDate = new Date();
        // remove 30 seconds for jitter, make sure this works for at least
        // 30 seconds so we don't have hard loops
        this._challengeExpiry = parseInt(json.expires_in, 10) - 30;
        if (this._challengeExpiry < 30) {
          this._challengeExpiry = 30;
        }

        this.setProperties({
          accountHoneypot: json.value,
          accountChallenge: json.challenge.split("").reverse().join(""),
        });
      })
      .finally(() => (this._hpPromise = undefined));

    return this._hpPromise;
  }

  handleSkipConfirmation() {
    if (this.skipConfirmation) {
      this.performAccountCreation().finally(
        () => (this.skipConfirmation = false)
      );
    }
  }

  performAccountCreation() {
    if (
      !this._challengeDate ||
      new Date() - this._challengeDate > 1000 * this._challengeExpiry
    ) {
      return this.fetchConfirmationValue().then(() =>
        this.performAccountCreation()
      );
    }

    const attrs = {
      accountName: this.accountName,
      accountEmail: this.accountEmail,
      accountPassword: this.accountPassword,
      accountUsername: this.accountUsername,
      accountChallenge: this.accountChallenge,
      inviteCode: this.inviteCode,
      accountPasswordConfirm: this.accountHoneypot,
    };

    // Add the userFields to the data
    if (!isEmpty(this.userFields)) {
      attrs.userFields = {};
      this.userFields.forEach((f) => (attrs.userFields[f.field.id] = f.value));
    }

    this.set("formSubmitted", true);
    return User.createAccount(attrs).then(
      (result) => {
        if (this.isDestroying) {
          return;
        }

        this.isDeveloper = false;
        if (result.success) {
          // invalidate honeypot
          this._challengeExpiry = 1;

          // Trigger the browser's password manager using the hidden static login form:
          const _form = document.getElementById("hidden-login-form");
          if (_form) {
            const set = (key, value) => {
              _form.querySelector(`input[name=${key}]`).value = value;
            };

            set("username", this.accountUsername);
            set("password", this.accountPassword);

            let { destination_url } = this.authOptions || {};

            if (destination_url && destination_url !== "/signup") {
              set("redirect", destination_url);
            } else {
              set("redirect", userPath("account-created"));
            }

            _form.submit();
          }

          return new Promise(() => {}); // This will never resolve, the page will reload instead
        } else {
          this.set("flash", result.message || i18n("create_account.failed"));
          if (result.is_developer) {
            this.isDeveloper = true;
          }
          if (result.errors?.["user_password.password"]?.length > 0) {
            this.passwordValidationHelper.rejectedPasswords.push(
              attrs.accountPassword
            );
          }
          this.set("formSubmitted", false);
          removeCookie("destination_url");
        }
      },
      () => {
        this.set("formSubmitted", false);
        removeCookie("destination_url");
        return this.set("flash", i18n("create_account.failed"));
      }
    );
  }

  @action
  scrollInputIntoView(event) {
    event.target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }

  @action
  togglePasswordMask() {
    this.toggleProperty("maskPassword");
  }

  @action
  externalLogin(provider) {
    // we will automatically redirect to the external auth service
    this.login.externalLogin(provider, { signup: true });
  }

  @action
  goToLogin() {
    const loginName = this.accountEmail || this.accountUsername;
    this.send("showLogin", { loginName });
  }

  @action
  createAccount() {
    this.set("flash", "");
    this.nameValidationHelper.forceValidationReason = true;
    this.userFieldsValidationHelper.validationVisible = true;
    this.set("emailValidationVisible", true);

    const validation = [
      this.emailValidation,
      this.usernameValidation,
      this.nameValidation,
      this.passwordValidation,
      this.userFieldsValidation,
    ].find((v) => v.failed);

    if (validation) {
      const element = validation.element;
      if (element) {
        if (element.tagName === "DIV") {
          if (element.scrollIntoView) {
            element.scrollIntoView();
          }
          element.click();
        } else {
          element.focus();
        }
      }

      return;
    }

    this.userFieldsValidationHelper.validationVisible = false;
    this.nameValidationHelper.forceValidationReason = false;
    this.performAccountCreation();
  }
}
